import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  DEFAULT_EMPTY_PLOT,
  plotsAreEquivalentWhenSerialized,
  plotToQueryString,
  readPlotFromQueryString,
} from "../utils";
import { isCompletePlot } from "../validation";
import {
  useClickHandlers,
  useContextBuilder,
  useCanShowIdentityLine,
} from "../hooks";
import {
  DataExplorerPlotConfig,
  PartialDataExplorerPlotConfig,
} from "@depmap/types";
import {
  logInitialPlot,
  logDirectPlotChange,
  logReducerTransform,
} from "../debug";
import plotConfigReducer, {
  PlotConfigReducerAction,
} from "../reducers/plotConfigReducer";
import ConfigurationPanel from "./ConfigurationPanel";
import VisualizationPanel from "./VisualizationPanel";
import styles from "../styles/DataExplorer2.scss";

interface Props {
  initialPlot: PartialDataExplorerPlotConfig;
  feedbackUrl: string | null;
  contactEmail: string;
  tutorialLink: string;
}

function DataExplorer2MainContent({
  initialPlot,
  feedbackUrl,
  contactEmail,
  tutorialLink,
}: Props) {
  useEffect(() => {
    logInitialPlot(initialPlot);
  }, [initialPlot]);

  const reactKey = useRef(0);

  const [isInitialPageLoad, setIsInitialPageLoad] = useState(
    initialPlot === DEFAULT_EMPTY_PLOT
  );
  const [plot, dispatchPlotAction] = useReducer(plotConfigReducer, initialPlot);

  const setPlot = (nextPlot: DataExplorerPlotConfig) =>
    dispatchPlotAction({ type: "set_plot", payload: nextPlot });

  // DimensionSelectV2's known double-dispatch (see its own comments) fires
  // two calls here for one click, both closing over the same stale `plot`
  // and so computing the same `nextPlot`. Without this queue, both calls'
  // `readPlotFromQueryString` reads race each other and can both see the
  // pre-push URL, so both decide they differ from it and both push —
  // one real history entry plus a redundant duplicate. Chaining onto the
  // previous call's promise instead of running concurrently guarantees the
  // second call's read happens after the first call's pushState (if any),
  // so it correctly finds nothing left to push.
  const historyQueueRef = useRef<Promise<void>>(Promise.resolve());

  const dispatchPlotActionAndUpdateHistory = useCallback(
    (action: PlotConfigReducerAction) => {
      dispatchPlotAction(action);
      const nextPlot = plotConfigReducer(plot, action);
      logReducerTransform(action, plot, nextPlot);

      if (!isCompletePlot(nextPlot)) {
        return;
      }

      setIsInitialPageLoad(false);

      historyQueueRef.current = historyQueueRef.current.then(async () => {
        const prevPlot = await readPlotFromQueryString();

        if (!plotsAreEquivalentWhenSerialized(prevPlot, nextPlot)) {
          const queryString = await plotToQueryString(nextPlot);
          window.history.pushState(null, "", queryString);
        }
      });
    },
    [plot]
  );

  useEffect(() => {
    const onClickExample = (e: Event) => {
      // WORKAROUND: The DimensionSelectV2 has some very hacky internal state
      // that gets confused when you go from an uninitialized plot to a valid
      // plot like this. We'll work around this by forcing it to re-mount.
      reactKey.current++;

      dispatchPlotActionAndUpdateHistory({
        type: "set_plot",
        payload: (e as CustomEvent).detail,
      });
    };

    window.addEventListener("dx2_example_clicked", onClickExample);

    return () => {
      window.removeEventListener("dx2_example_clicked", onClickExample);
    };
  }, [dispatchPlotActionAndUpdateHistory]);

  // The category picker sits beside the legend, in the visualization column,
  // which has no dispatch of its own — so it gets a callback instead, in the
  // same style as the other plot-side config edits.
  const handleChangeExpansionMembers = useCallback(
    (members: string[] | null) => {
      dispatchPlotActionAndUpdateHistory({
        type: "select_expansion_members",
        payload: members,
      });
    },
    [dispatchPlotActionAndUpdateHistory]
  );

  const handleChangeCategories = useCallback(
    (target: "color" | "facet", categories: string[] | null) => {
      dispatchPlotActionAndUpdateHistory({
        type: "select_categories",
        payload: { target, categories },
      });
    },
    [dispatchPlotActionAndUpdateHistory]
  );

  useEffect(() => {
    const onPopState = (e: PopStateEvent) => {
      readPlotFromQueryString().then((nextPlot) => {
        if (nextPlot === DEFAULT_EMPTY_PLOT) {
          reactKey.current++;
        }

        setPlot(nextPlot);
        logDirectPlotChange("onPopState", plot, nextPlot);

        const initial = window.location.search.substr(1) === "";
        setIsInitialPageLoad(initial);

        if (e.state?.startScreenScrollTop) {
          const el = document.querySelector("#dx2_start_screen");
          el!.scroll(0, e.state.startScreenScrollTop);
        }
      });
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [plot]);

  const {
    ContextBuilder,
    onClickSaveAsContext,
    onClickCreateContext,
  } = useContextBuilder(plot as DataExplorerPlotConfig, setPlot);

  const {
    handleClickSaveSelectionAsContext,
    handleClickVisualizeSelected,
    handleClickColorByContext,
    handleClickShowDensityFallback,
    handleClickCopyAxisConfig,
    handleClickSwapAxisConfigs,
  } = useClickHandlers(
    plot as DataExplorerPlotConfig,
    setPlot,
    onClickSaveAsContext
  );

  const canShowIdentityLine = useCanShowIdentityLine(
    plot?.dimensions?.x?.dataset_id,
    plot?.dimensions?.y?.dataset_id
  );

  return (
    <>
      <main className={styles.DataExplorer2}>
        <ConfigurationPanel
          key={reactKey.current}
          plot={plot}
          dispatch={dispatchPlotActionAndUpdateHistory}
          canShowIdentityLine={canShowIdentityLine}
          onClickSaveAsContext={onClickSaveAsContext}
          onClickCreateContext={onClickCreateContext}
          onClickCopyAxisConfig={handleClickCopyAxisConfig}
          onClickSwapAxisConfigs={handleClickSwapAxisConfigs}
        />
        <VisualizationPanel
          plotConfig={isCompletePlot(plot) ? plot : null}
          canShowIdentityLine={canShowIdentityLine}
          isInitialPageLoad={isInitialPageLoad}
          onClickVisualizeSelected={handleClickVisualizeSelected}
          onClickSaveSelectionAsContext={handleClickSaveSelectionAsContext}
          onClickColorByContext={handleClickColorByContext}
          onChangeCategories={handleChangeCategories}
          onChangeExpansionMembers={handleChangeExpansionMembers}
          onClickShowDensityFallback={handleClickShowDensityFallback}
          feedbackUrl={feedbackUrl}
          contactEmail={contactEmail}
          tutorialLink={tutorialLink}
        />
      </main>
      <ContextBuilder />
    </>
  );
}

export default DataExplorer2MainContent;
